import { FormModal, SubmitProps } from '@/components/form-modal';

import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildNameLikeSelectOptions,
  buildUserSelectOptions,
  emptySelectOption,
} from '@/components/ui/multi-select-utils';
import {
  createClassTime,
  createStudentGroup,
  EmptyArray,
  getCoaches,
  getSchedules,
  getStudentGroup,
  getStudentGroups,
  updateStudentGroup,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  adjustMinimalClassTimeAcrossTZ,
  CreateStudentGroupOptions,
  MAX_PAGE_SIZE,
  encodeClassTimeMeetingTimePair,
  OnboardingSessionPlaceholderDay,
  Schedule,
  StudentGroupWithScheduleAndLevel,
  UpdateStudentGroupOptions,
} from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AllUpdateTeacherTypeValues } from '@/lib/coach-utils';
import { formCreateEditOneTimeClassSchema } from '@/lib/student-group-utils';
import { formatClassTimeMeetingTimePairLabel, FrequentTimeZones, intFromString } from '@airreading/common';
import { ReactNode } from 'react';
import { DefaultValues } from 'react-hook-form';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { CreateEditOneTimeClassForm } from './create-edit-one-time-class-form';

function classTimeMeetingTimePairFromValue(
  value: string | undefined,
): { startPlainTime: Temporal.PlainTime; endPlainTime: Temporal.PlainTime } | undefined {
  if (!value) return undefined;
  const [start, end] = value.split('|');
  if (!start || !end) return undefined;
  return {
    startPlainTime: Temporal.PlainTime.from(start),
    endPlainTime: Temporal.PlainTime.from(end),
  };
}

function defaultClassTimePlainTimeSelectCt(from: StudentGroupWithScheduleAndLevel | undefined, schoolTimeZone: string) {
  if (!from?.classTime) return emptySelectOption;
  const adjusted = adjustMinimalClassTimeAcrossTZ(from.classTime, schoolTimeZone, FrequentTimeZones.UsCentral);
  const first = adjusted.meetingBlocks[0];
  if (!first) return emptySelectOption;
  return {
    label: formatClassTimeMeetingTimePairLabel(first.startPlainTime, first.endPlainTime),
    value: encodeClassTimeMeetingTimePair(first.startPlainTime, first.endPlainTime),
  };
}

async function findOrCreateOneTimeClassTime(
  startPlainTimeSchool: Temporal.PlainTime,
  endPlainTimeSchool: Temporal.PlainTime,
  schedules: Schedule[],
  preferredScheduleId: string | undefined,
  createClassTimeMutate: typeof createClassTime,
): Promise<string> {
  const existingClassTime = schedules
    .flatMap((schedule) => schedule.classTimes)
    .find(
      (ct) =>
        ct.type === 'one_time' &&
        ct.meetingBlocks.length === 1 &&
        ct.meetingBlocks[0].startPlainTime.toString() === startPlainTimeSchool.toString() &&
        ct.meetingBlocks[0].endPlainTime.toString() === endPlainTimeSchool.toString(),
    );

  if (existingClassTime) {
    return existingClassTime.id;
  }

  const schedule =
    (preferredScheduleId ? schedules.find((s) => s.id === preferredScheduleId) : undefined) ?? schedules[0];

  const newClassTime = await createClassTimeMutate({
    scheduleId: schedule.id,
    meetingBlocks: [
      {
        day: OnboardingSessionPlaceholderDay,
        startPlainTime: startPlainTimeSchool,
        endPlainTime: endPlainTimeSchool,
      },
    ],
    type: 'one_time',
  });
  return newClassTime.id;
}

interface CreateEditOneTimeClassProps {
  trigger: ReactNode;
  schoolId: string;
  from?: StudentGroupWithScheduleAndLevel;
  schoolTimeZone: string;
}

export const CreateEditOneTimeClass = ({ trigger, schoolId, from, schoolTimeZone }: CreateEditOneTimeClassProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { mutateAsync: createMutate } = useMutation({
    mutationFn: createStudentGroup,
  });

  const { mutateAsync: updateMutate } = useMutation({
    mutationFn: updateStudentGroup,
  });

  const { mutateAsync: createClassTimeMutate } = useMutation({
    mutationFn: createClassTime,
  });

  const onSubmit = async ({ formData, closeModal }: SubmitProps<z.infer<typeof formCreateEditOneTimeClassSchema>>) => {
    const {
      coach,
      students,
      overrideTime,
      overrideStartPlainDate,
      overrideStartPlainTime,
      classTime,
      overrideStartDurationInMin,
      learningPath,
      startingLesson,
      teacherType,
      coachAvailable,
      coachAll,
      notes,
    } = formData;

    const selectedFromCoach = teacherType === AllUpdateTeacherTypeValues.Available ? coachAvailable : coachAll;
    const hasNoCoach = !selectedFromCoach || !selectedFromCoach.value;

    if (from && hasNoCoach) {
      toast({
        title: 'Invalid teacher',
        description: 'Please select a teacher for the class.',
      });
      return;
    }

    const selectedCoach = selectedFromCoach || coach;

    let classTimeId: string | undefined;
    try {
      if (!overrideTime && from?.classTime?.schedule?.classTimes) {
        const matchingClassTime = from.classTime.schedule.classTimes.find((ct) => {
          if (ct.type !== 'one_time') return false;
          const firstMeetingBlock = adjustMinimalClassTimeAcrossTZ(ct, schoolTimeZone, FrequentTimeZones.UsCentral)
            .meetingBlocks[0];
          if (!firstMeetingBlock) return false;
          return (
            encodeClassTimeMeetingTimePair(firstMeetingBlock.startPlainTime, firstMeetingBlock.endPlainTime) ===
            classTime.value
          );
        });
        if (matchingClassTime) {
          classTimeId = matchingClassTime.id;
        }
      }

      if (!classTimeId) {
        const overrideDurationInMin = intFromString(overrideStartDurationInMin);
        const selectedMeetingTimePair = overrideTime
          ? overrideStartPlainTime
            ? {
                startPlainTime: overrideStartPlainTime,
                endPlainTime: overrideStartPlainTime.add({ minutes: overrideDurationInMin }),
              }
            : undefined
          : classTimeMeetingTimePairFromValue(classTime.value);

        if (!selectedMeetingTimePair) {
          toast({
            title: 'Missing start time',
            description: 'Please select class time.',
            variant: 'destructive',
          });
          return;
        }

        const plainDateForTz =
          overrideStartPlainDate ??
          from?.overrideStartPlainDate ??
          from?.classTime?.schedule?.startPlainDate ??
          undefined;
        if (!plainDateForTz) {
          toast({
            title: 'Missing class date',
            description:
              'A class date is required to convert the meeting time to the school time zone. Please select a class date.',
            variant: 'destructive',
          });
          return;
        }

        const start = plainDateForTz.toZonedDateTime({
          plainTime: selectedMeetingTimePair.startPlainTime,
          timeZone: FrequentTimeZones.UsCentral,
        });
        const end = plainDateForTz.toZonedDateTime({
          plainTime: selectedMeetingTimePair.endPlainTime,
          timeZone: FrequentTimeZones.UsCentral,
        });
        const startPlainTimeInSchoolTimeZone = start.withTimeZone(schoolTimeZone).toPlainTime();
        const endPlainTimeInSchoolTimeZone = end.withTimeZone(schoolTimeZone).toPlainTime();

        const schedulesResponse = await getSchedules({ pageIndex: 0, pageSize: MAX_PAGE_SIZE, schoolId }).queryFn();
        const schedules: Schedule[] = schedulesResponse.schedules ?? EmptyArray;

        if (!schedules.length) {
          toast({
            title: 'No schedule configured',
            description: 'Please ensure the school has a schedule configured before saving this class.',
            variant: 'destructive',
          });
          return;
        }

        classTimeId = await findOrCreateOneTimeClassTime(
          startPlainTimeInSchoolTimeZone,
          endPlainTimeInSchoolTimeZone,
          schedules,
          from?.classTime?.schedule?.id,
          createClassTimeMutate,
        );
      }
    } catch (error) {
      toast({
        title: `Error ${from ? 'updating' : 'creating'} one-time class`,
        description: airServerErrorFrom(error)?.userFacingMessage || '',
        variant: 'destructive',
      });
      return;
    }
    if (!classTimeId) {
      toast({
        title: `Error ${from ? 'updating' : 'creating'} one-time class`,
        description: 'Unable to determine class time.',
        variant: 'destructive',
      });
      return;
    }

    const commonFields: Pick<
      CreateStudentGroupOptions,
      | 'status'
      | 'coachId'
      | 'studentIds'
      | 'overrideStartPlainDate'
      | 'overrideEndPlainDate'
      | 'classTimeId'
      | 'learningPathId'
      | 'startingLessonId'
    > = {
      status: from?.status || 'active',
      coachId: selectedCoach?.value,
      studentIds: students.map((student) => student.value),
      overrideStartPlainDate,
      overrideEndPlainDate: overrideStartPlainDate,
      classTimeId,
      learningPathId: learningPath.value,
      startingLessonId: startingLesson.value,
    };

    closeModal();

    const apiCall = from
      ? () =>
          updateMutate({
            id: from.id,
            data: {
              ...commonFields,
              attendanceStatus: from.attendanceStatus ?? null,
              emailsSent: from.emailsSent ?? [],
              coachUnassignedAtPlainDate: from.coachLastUnassignedAtPlainDate ?? undefined,
              attendanceNotes: from.attendanceNotes ?? null,
              notes: notes ?? null,
            } satisfies UpdateStudentGroupOptions,
          })
      : () => createMutate(commonFields);

    return apiCall()
      .then(() => {
        if (from) {
          queryClient.invalidateQueries(getStudentGroup(from.id));
          queryClient.invalidateQueries(getCoaches());
        } else {
          queryClient.invalidateQueries(getStudentGroups());
        }

        queryClient.invalidateQueries({ queryKey: ['AVAILABLE_COACHES'] });

        toast({
          title: `One-time class ${from ? 'updated' : 'created'} successfully`,
        });
        closeModal();
      })
      .catch((error) => {
        toast({
          title: `Error ${from ? 'updating' : 'creating'} one-time class`,
          description: airServerErrorFrom(error)?.userFacingMessage || '',
        });
      });
  };

  const defaultValues: DefaultValues<z.infer<typeof formCreateEditOneTimeClassSchema>> = {
    coach: from?.coach ? buildUserSelectOptions(from.coach) : emptySelectOption,
    students: from?.students ? from.students.map(buildUserSelectOptions) : [],
    schedule: from?.classTime.schedule
      ? buildNameLikeSelectOptions({ id: from.classTime.schedule.id, name: from.classTime.schedule.name })
      : emptySelectOption,
    classTime: defaultClassTimePlainTimeSelectCt(from, schoolTimeZone),
    overrideTime: false,
    overrideStartPlainDate: from?.overrideStartPlainDate ? from.overrideStartPlainDate : undefined,
    overrideStartPlainTime: Temporal.PlainTime.from('08:00:00'),
    overrideStartDurationInMin: '30',
    learningPath: from?.learningPath ? buildNameLikeSelectOptions(from.learningPath) : emptySelectOption,
    startingLesson: from?.startingLesson ? buildNameLikeSelectOptions(from.startingLesson) : emptySelectOption,
    teacherType: AllUpdateTeacherTypeValues.Available,
    coachAvailable: from?.coach ? buildUserSelectOptions(from.coach) : emptySelectOption,
    notes: from?.notes,
  };

  return (
    <FormModal
      modalTitle={`${from ? 'Edit' : 'Add'} One-Time Class`}
      formSchema={formCreateEditOneTimeClassSchema}
      onSubmit={onSubmit}
      trigger={trigger}
      defaultValues={defaultValues}
      confirmBtnLabel={from ? 'Save' : 'Add'}
      validateOnOpen={!!from}
      dialogContentProps={{ className: 'lg:max-w-screen-sm' }}
    >
      {({ form }) => {
        return (
          <CreateEditOneTimeClassForm form={form} schoolId={schoolId} schoolTimeZone={schoolTimeZone} from={from} />
        );
      }}
    </FormModal>
  );
};
